import {
  ProfileMessage,
  ProfileMessageType,
  websocket,
  Profile,
  MatchmakingMessageType,
  InvitationMessageType,
} from '@playcastdotio/Messaging';
import { sendMessage } from '..';
import {
  authState,
  matchmakeToPublicMachineState,
  networkMetricsState,
  ownerAliasState,
  profileState,
  regionOverrideState,
} from '../../../stores/shared';
import { getPersistentPlayerId } from '../../../utilities/persistentId';
import { serviceContainer } from '../../../../services/ServiceContainer';

// Track when we're expecting an invitation creation response
let pendingInvitationCreation = false;

const unsubscribeFromInvitations = (invitationIds: string[]) => {
  if (invitationIds.length === 0) return;

  sendMessage(
    websocket.generators.invitation(InvitationMessageType.INVITATION_UNSUBSCRIBE, {
      invitationIds,
    }),
  );
};

export const handleProfileMessage = (message: ProfileMessage) => {
  console.log('Profile message:', message);
  switch (message.type) {
    case ProfileMessageType.PROFILE_GET_RESPONSE: {
      console.log('Profile get response:', message.profile);
      profileState.updateState({
        ...profileState.currentState,
        profile: message.profile || null,
        isLoading: false,
        error: message.profile ? null : 'Profile not found',
      });
      break;
    }
    case ProfileMessageType.PROFILE_UPDATE_SUCCESS: {
      if (message.profile) {
        console.log('Profile update success:', message.profile, message.isOwner);
        profileState.updateState({
          ...profileState.currentState,
          profile: message.profile,
          isOwner: true,
          isLoading: false,
          error: null,
        });
      }
      console.log('Profile update success');
      break;
    }
    case ProfileMessageType.PROFILE_UPDATE_ERROR: {
      console.log('Profile update error:', message.error);
      break;
    }
    case ProfileMessageType.PROFILE_GET_ALIASES_RESPONSE: {
      console.log('Profile get aliases response:', message.aliases);
      ownerAliasState.updateState({
        success: message.success,
        aliases: {
          ...ownerAliasState.currentState?.aliases,
          ...message.aliases,
        },
        usernames: {
          ...ownerAliasState.currentState?.usernames,
          ...message.usernames,
        },
      });
      break;
    }
    case ProfileMessageType.PROFILE_GET_INVITATIONS_RESPONSE: {
      console.log('Profile get invitations response:', message.invitations);

      // Find invitations that are no longer in the list
      const oldInvitationIds = profileState.currentState.invitations.map((inv) => inv.id);
      const newInvitationIds = message.invitations.map((inv) => inv.id);
      const removedInvitationIds = oldInvitationIds.filter((id) => !newInvitationIds.includes(id));

      // Unsubscribe from removed invitations
      unsubscribeFromInvitations(removedInvitationIds);

      profileState.updateState({
        ...profileState.currentState,
        invitations: message.invitations || [],
        nextCursor: message.nextCursor,
        invitationsLoading: false,
        invitationsError: undefined,
      });
      break;
    }
    case ProfileMessageType.PROFILE_GET_INVITATIONS_ERROR: {
      console.log('Profile get invitations error:', message.error);

      // Unsubscribe from all current invitations since we're clearing the list
      const currentInvitationIds = profileState.currentState.invitations.map((inv) => inv.id);
      unsubscribeFromInvitations(currentInvitationIds);

      profileState.updateState({
        ...profileState.currentState,
        invitations: [],
        nextCursor: undefined,
        invitationsLoading: false,
        invitationsError: message.error?.message || 'Failed to load invitations',
      });
      break;
    }
    case ProfileMessageType.PROFILE_GET_ERROR: {
      console.log('Profile get error:', message.error);
      // Set a retry after 2 seconds
      setTimeout(() => {
        queryProfile();
      }, 2000);
      break;
    }
    case ProfileMessageType.PROFILE_GET_ALIASES_ERROR: {
      console.log('Profile get aliases error:', message.error);
      // Set a retry after 2 seconds
      setTimeout(() => {
        queryOwnerAliases();
      }, 2000);
      break;
    }
    case ProfileMessageType.PROFILE_GET:
    case ProfileMessageType.PROFILE_UPDATE:
    case ProfileMessageType.PROFILE_GET_INVITATIONS: {
      console.warn('Server received message it should not have received');
      console.log(message);
      break;
    }
  }
};

let debounceTimeout: NodeJS.Timeout | null = null;

export const queryProfile = () => {
  if (!authState.currentState.hostAuthId) {
    console.error('No host auth ID found');
    return;
  }

  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }

  debounceTimeout = setTimeout(() => {
    profileState.updateState({
      ...profileState.currentState,
      isLoading: true,
      error: null,
    });

    if(authState.currentState.hostAuthId.startsWith('anon_')) {
      return;
    }

    sendMessage(websocket.generators.profile(ProfileMessageType.PROFILE_GET, {}));
  }, 50);
};

export const queryOwnerAliases = () => {
  if (!authState.currentState.hostAuthId) {
    console.error('No host auth ID found');
    return;
  }
  if(authState.currentState.hostAuthId.startsWith('anon_')) {
    return;
  }
  const msg = websocket.generators.profile(ProfileMessageType.PROFILE_GET_ALIASES, {});
  sendMessage(msg);
};

export const updateProfile = (profile: Profile) => {
  if (!authState.currentState.hostAuthId) {
    console.error('No host auth ID found');
    return;
  }

  sendMessage(
    websocket.generators.profile(ProfileMessageType.PROFILE_UPDATE, {
      profile,
    }),
  );
};

export const queryProfileInvitations = (cursor?: string, limit = 16, search?: string) => {
  if (!authState.currentState.hostAuthId) {
    console.error('No host auth ID found');
    return;
  }

  profileState.updateState({
    ...profileState.currentState,
    invitationsLoading: profileState.currentState.invitations?.length > 0,
    invitationsError: undefined,
  });

  sendMessage(
    websocket.generators.profile(ProfileMessageType.PROFILE_GET_INVITATIONS, {
      cursor,
      limit,
      invitationType: 'cafe',
      search,
    }),
  );
};

export const createMatchmakerLobby = () => {
  if (!authState.currentState.hostAuthId) {
    console.error('No host auth ID found');
    return;
  }
  // Set flag to indicate we're creating an invitation
  pendingInvitationCreation = true;

  const msg = websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_CREATE_INVITATION, {
    playerInfo: {
      displayName: profileState.currentState.profile?.displayName || '',
    },
    matchmakeToPublicMachine: matchmakeToPublicMachineState.currentState.matchmakeToPublicMachine,
    sessionPreset: {
      authRequired: false,
      defaultPlayerSettings: {
        gamepad: true,
        keyboard: false,
        mouse: false,
      },
    },
  });
  sendMessage(msg);
};

export const joinMatchmakerLobby = () => {
  if (!authState.currentState.hostAuthId) {
    console.error('No host auth ID found');
    return;
  }
  if (!profileState.currentState.selectedInvitationId) {
    console.error('No invitation ID found');
    return;
  }
  const msg = websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_JOIN_INVITATION, {
    invitationId: profileState.currentState.selectedInvitationId,
    playerInfo: {
      displayName: profileState.currentState.profile?.displayName || '',
      playerId: authState.currentState.hostAuthId,
    },
  });
  sendMessage(msg);
};

export const leaveMatchmakerLobby = () => {
  if (!authState.currentState.hostAuthId) {
    console.error('No host auth ID found');
    return;
  }
  if (!profileState.currentState.selectedInvitationId) {
    console.error('No invitation ID found');
    return;
  }
  // Check we are actually in the invitation.
  const selectedInvitation = profileState.currentState.invitations.find(
    (invitation) => invitation.id === profileState.currentState.selectedInvitationId,
  );
  const inInvitation =
    selectedInvitation?.type === 'cafe' &&
    selectedInvitation.manifest.players.some((player) => player.playerId === authState.currentState.hostAuthId);
  if (!inInvitation) {
    console.error('We are not in the invitation');
    return;
  }

  const msg = websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_LEAVE_INVITATION, {
    invitationId: profileState.currentState.selectedInvitationId,
    playerId: authState.currentState.hostAuthId,
  });
  sendMessage(msg);
};

export const startMatchmakerLobby = (useSteamGuestLogin?: boolean, regionOverride?: string) => {
  if (!authState.currentState.hostAuthId) {
    console.error('No host auth ID found');
    return;
  }
  if (!profileState.currentState.selectedInvitationId) {
    console.error('No invitation ID found');
    return;
  }
  const msg = websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_ADD_TO_QUEUE, {
    invitationId: profileState.currentState.selectedInvitationId,
    useSteamGuestLogin,
    regionOverride: regionOverride || regionOverrideState.currentState.regionOverride,
  });
  sendMessage(msg);
};

export const destroyMatchmakerLobby = (selectedInvitationId?: string) => {
  if (!selectedInvitationId) {
    console.error('No invitation ID found');
    return;
  }
  const msg = websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_INVALIDATE_SESSION, {
    invitationId: selectedInvitationId,
    reason: 'Host destroyed lobby',
  });
  sendMessage(msg);
};

// Export the flag for use in matchmaker.ts
export const isPendingInvitationCreation = () => pendingInvitationCreation;
export const clearPendingInvitationCreation = () => {
  pendingInvitationCreation = false;
};
